import { NEXT_PUBLIC_GRAPHQL_URL } from '@jusbrasil-web/shared-apollo-client'
import { useCallback, useEffect, useRef } from 'react'

import { IS_DEV_ENVIRONMENT } from '../../constants'

type ExperimentConfig = {
  group: string
  name: string
}

type UseExperimentCheckProps = {
  auto?: boolean
  experimentConfig: ExperimentConfig
}

export const useExperimentCheck = ({ auto = true, experimentConfig }: UseExperimentCheckProps) => {
  const controllerRef = useRef<AbortController | null>(null)

  const invokeExperiment = useCallback(async () => {
    try {
      if (IS_DEV_ENVIRONMENT) {
        return
      }

      controllerRef.current?.abort()
      controllerRef.current = new AbortController()

      const response = await fetch(NEXT_PUBLIC_GRAPHQL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controllerRef.current.signal,
        body: JSON.stringify({
          query: `
              query ExperimentData {
                root {
                  participateExperiment(experimentGroup: "${experimentConfig.group}", experimentName: "${experimentConfig.name}") {
                    participating
                    alternative {
                      name
                    }
                  }
                }
              }
            `,
        }),
      })

      if (controllerRef.current.signal.aborted) return
      if (!response.ok) throw new Error(`[useExperimentCheck] Network error: ${response.status}`)

      const parsedResponse = await response.json()

      if (parsedResponse?.errors?.length) {
        const errorMessage = parsedResponse?.errors.reduce(
          (acc: string, error: { message: string }) => {
            acc += `\n${error.message}`
            return acc
          },
          ''
        )

        throw new Error(errorMessage)
      }

      const result = parsedResponse?.data?.root?.participateExperiment
      if (!result) {
        throw new Error('[useExperimentCheck] Invalid response structure')
      }
    } catch (error) {
      console.error('[useExperimentCheck] Error invoking experiment', error)
    }
  }, [])

  useEffect(() => {
    if (!auto) return

    invokeExperiment()

    return () => {
      controllerRef.current?.abort()
    }
  }, [auto, invokeExperiment])

  return { invokeExperiment }
}
